import React, { useState, useEffect, useCallback } from 'react';
import '../styles/PriceTicker.css';

// Use Binance public API - no auth needed, very fast and accurate
const BINANCE_API = 'https://api.binance.com/api/v3';

// Top coins to display
const TICKER_SYMBOLS = [
  'BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'XRPUSDT', 'DOGEUSDT', 
  'ADAUSDT', 'AVAXUSDT', 'LINKUSDT', 'DOTUSDT', 'MATICUSDT'
];

function PriceTicker() {
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPrices = useCallback(async () => {
    try {
      // Fetch 24hr ticker for all symbols in one call
      const symbols = JSON.stringify(TICKER_SYMBOLS);
      const response = await fetch(
        `${BINANCE_API}/ticker/24hr?symbols=${encodeURIComponent(symbols)}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch prices');
      
      const data = await response.json();
      
      const priceMap = {};
      data.forEach(ticker => {
        const symbol = ticker.symbol.replace('USDT', '');
        priceMap[symbol] = {
          price: parseFloat(ticker.lastPrice),
          change: parseFloat(ticker.priceChangePercent),
          high: parseFloat(ticker.highPrice),
          low: parseFloat(ticker.lowPrice),
          volume: parseFloat(ticker.volume)
        };
      });
      
      setPrices(priceMap);
      setError(null);
    } catch (err) {
      console.error('Price fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrices();
    // Refresh every 2 seconds for real-time feel
    const interval = setInterval(fetchPrices, 2000);
    return () => clearInterval(interval);
  }, [fetchPrices]);

  const formatPrice = (price) => {
    if (price >= 1000) return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (price >= 1) return price.toFixed(4);
    return price.toFixed(6);
  };

  const formatChange = (change) => {
    return change >= 0 ? `+${change.toFixed(2)}%` : `${change.toFixed(2)}%`;
  };

  const tickerItems = Object.entries(prices).map(([symbol, data]) => ({
    symbol,
    ...data
  }));

  if (loading && tickerItems.length === 0) {
    return (
      <div className="price-ticker loading-ticker">
        <span>Loading prices...</span>
      </div>
    );
  }

  if (error && tickerItems.length === 0) {
    return null; // Hide if error and no cached data
  }

  return (
    <div className="price-ticker">
      <div className="ticker-track">
        <div className="ticker-content">
          {/* Duplicate items for seamless infinite scroll */}
          {[...tickerItems, ...tickerItems].map((item, idx) => (
            <div key={idx} className="ticker-item">
              <span className="ticker-symbol">{item.symbol}</span>
              <span className="ticker-price">${formatPrice(item.price)}</span>
              <span className={`ticker-change ${item.change >= 0 ? 'positive' : 'negative'}`}>
                {formatChange(item.change)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PriceTicker;
